<template>
  <v-bottom-sheet
    v-model="internalVisible"
    fullscreen
    @update:model-value="handleVisibilityChange"
  >
    <div class="mobile-filter-sheet">
      <!-- Header -->
      <div class="sheet-header">
        <h3 class="header-title">Filters</h3>
        <button
          type="button"
          class="close-button"
          aria-label="Close filters"
          @click="closeSheet"
        >
          <img src="/images/close.svg" alt="Close" />
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="sheet-content">
        <ExpandedFilterComponent
          :selectedFilters="selectedFilters"
          :initialFilters="expandedInitialFilters || initialFilters"
          :locale="locale"
          :subjectOptions="subjectOptions"
          :gradeOptions="gradeOptions"
          :hardwareOptions="hardwareOptions"
          :durationOptions="durationOptions"
          :tagOptions="tagOptions"
          @filtersChanged="onFiltersChanged"
          @close="closeSheet"
        />
      </div>

      <!-- Footer Actions -->
      <div class="sheet-footer">
        <button type="button" class="clear-button" @click="clearAllFilters">
          Clear All
        </button>
        <button type="button" class="apply-button" @click="applyFilters">
          Apply Filters
        </button>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import ExpandedFilterComponent from "@/components/maker-cart/ExpandedFilterComponent.vue";
import { useFilterLogic } from "@/composeables/useFilterLogic";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  selectedFilters: {
    type: Array,
    default: () => [],
  },
  initialFilters: {
    type: Object,
    default: () => ({
      subjects: [],
      grades: [],
      focus: [],
      hardware: [],
      duration: [],
      tags: [],
      sort: "newest",
      locale: "US",
      popular: false,
      free: false,
    }),
  },
  locale: {
    type: String,
    default: "US",
  },
});

const emit = defineEmits(["update:visible", "filtersChanged", "close"]);

// Internal visibility state
const internalVisible = ref(props.visible);

// Use the filter logic composable
const {
  subject,
  grade,
  hardware,
  duration,
  tags,
  popular,
  freeResources,
  expandedInitialFilters,
  subjectOptions,
  gradeOptions,
  durationOptions,
  hardwareOptions,
  tagOptions,
  onExpandedFiltersChanged,
} = useFilterLogic(props, emit);

// Store the latest filter changes
const latestFilterChanges = ref(null);

// Watch for visibility changes from parent
watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      // When opening, set up initial snapshot
      expandedInitialFilters.value = {
        subjects: [...subject.value],
        grades: [...grade.value],
        focus: [],
        hardware: [...hardware.value],
        duration: [...duration.value],
        tags: [...tags.value],
        sort: props.initialFilters.sort || "newest",
        locale: props.locale || "US",
        popular: Boolean(popular.value),
        free: Boolean(freeResources.value),
      };
    }
  }
);

const handleVisibilityChange = (newVal) => {
  if (!newVal) {
    closeSheet();
  }
};

const onFiltersChanged = (dataFilter) => {
  // Store the changes but don't emit yet
  latestFilterChanges.value = dataFilter;
  // Update local state
  onExpandedFiltersChanged(dataFilter);
};

const applyFilters = () => {
  // Emit the latest filter changes
  if (latestFilterChanges.value) {
    emit("filtersChanged", latestFilterChanges.value);
  }
  closeSheet();
};

const clearAllFilters = () => {
  // Reset all filters
  const emptyFilters = {
    subjects: [],
    grades: [],
    focus: [],
    hardwareAssociated: [],
    hardware: [],
    duration: [],
    tags: [],
    popular: false,
    free: false,
  };

  latestFilterChanges.value = emptyFilters;
  onExpandedFiltersChanged(emptyFilters);
  emit("filtersChanged", emptyFilters);
  closeSheet();
};

const closeSheet = () => {
  internalVisible.value = false;
  emit("update:visible", false);
  emit("close");
};
</script>

<style scoped>
.mobile-filter-sheet {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  background: white;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  color: var(--primary-blue);
  margin: 0;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.close-button img {
  width: 20px;
  height: 20px;
}

.sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 120px;
  -webkit-overflow-scrolling: touch;
}

.sheet-content :deep(.expanded-filter-component) {
  box-shadow: none;
  border-radius: 0;
}

.sheet-content :deep(.filter-header) {
  display: none;
}

.sheet-footer {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  background: white;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
}

.clear-button,
.apply-button {
  flex: 1;
  height: 48px;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.clear-button {
  background: white;
  border: 2px solid var(--primary-blue);
  color: var(--primary-blue);
}

.clear-button:hover {
  background: #f2f3fb;
}

.apply-button {
  background: var(--primary-blue);
  color: white;
}

.apply-button:hover {
  background: #002a4a;
}

/* Adjust expanded filter component for mobile */
.sheet-content :deep(.filter-content) {
  padding: 0;
  border-radius: 0;
  max-height: none;
}

.sheet-content :deep(.section-content) {
  max-height: none;
}

/* Make tags chips responsive */
.sheet-content :deep(.tag-chips) {
  max-width: 100%;
  padding-right: 0;
}
</style>
